import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { SkillSectionsChartService } from '@app/charts/components/skill-sections-chart/skill-sections-chart.service';
// services
import { FilterService } from '@app/shared/services/filter.service';
import { TranslateService } from '@ngx-translate/core';
import { DecimalPipe } from '@angular/common';
import { TranslateFn, translateWithDefaults } from '@app/shared/utils';

@Component({
  selector: 'dgx-flex-skill-sections-chart',
  templateUrl: './flex-skill-sections-chart.component.html',
})
export class FlexSkillSectionsChartComponent implements OnInit, OnChanges {
  @Input() public authUser;
  @Input() public displayName;
  @Input() public facets?: any;
  @Input() public includeFullManagerHierarchy?: boolean;
  @Input() public managerProfileKey?: number;
  @Input() public orgId: number;
  @Input() public orgName: string;
  @Input() public teamId?: number;
  @Input() public ratingType?: string;
  @Input() public results: any;
  @Input() public users: any;

  // Local - public

  public isViewAll?: boolean;
  public enableViewAll: boolean;
  public filteredResults;
  public ratingTypePrevSelection?: string;
  public chartName = '';
  public showMore = false;
  public groupId: string;
  public skillId: string;
  public canViewIndividualRatings = false;
  public i18n = this.translateService.instant([
    'SkillSectionsChart_ReviewCertificationNotAvailable',
    'SkillSectionsChart_AllSectionSkills',
    'SkillSectionsChart_PlanFollowersFormat',
    'SkillSectionsChart_PlanRatingsFormat',
    'SkillSectionsChart_NoRatings',
    'SkillSectionsChart_NoTarget',
    'SkillSectionsChart_SkillRatingsBreakdown',
    'SkillSectionsChart_SkillRatingsFormat',
    'SkillSectionsChart_NoData',
    'SkillRatingsChart_NoData',
    'SkillSectionsChart_Team_NoRatings',
  ]);
  public ratingTypeOptions =
    this.skillSectionsChartService.getRatingTypeOptions();
  private translate: TranslateFn;

  constructor(
    private filterService: FilterService,
    private translateService: TranslateService,
    private decimalPipe: DecimalPipe,
    public skillSectionsChartService: SkillSectionsChartService
  ) {
    this.translate = translateWithDefaults(this.translateService);
  }

  public ngOnInit(): void {
    this.chartName = this.i18n.SkillSectionsChart_SkillRatingsBreakdown;

    // For team, canViewIndividualRatings is always true.
    // If this is used in another context, a combination
    // canViewRating && !disableIndividualSkillsData
    // from AuthUser is likely what's needed. Just be
    // sure the team scenario is always true.
    this.canViewIndividualRatings = true;

    // Set default if ratingType is empty from the @Input
    if (!this.ratingType) {
      this.ratingType = this.ratingTypeOptions[0][0].Type;
    }

    if (this.facets?.length) {
      this.facets = this.filterService.filtersToFacets(this.facets);
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    this.filteredResults = this.results.filter(
      (x) => x.tagRatings !== undefined
    );
    setTimeout(() => {
      if (this.filteredResults && !!this.filteredResults.length) {
        this.getHighCharts(this.filteredResults, this.users);
      }
    });
  }

  public getHighCharts(results, users) {
    if (results && users) {
      const data = {
        items: [...results],
        users: [...users],
      };
      const context = this;
      this.skillSectionsChartService.generateChart(data, context);
    }
  }

  public getFollowFormat(skill) {
    const numberPercent = this.decimalPipe.transform(
      skill.followerPercent,
      '1.0-0'
    );

    return this.translate(
      '<span class="color-purple">[numberPercent]%</span> ([followersCount]) of team members follow this skill',
      'SkillSectionsChart_TeamMembers_FollowFormat',
      {
        followersCount: skill.followerCount,
        numberPercent,
      }
    );
  }

  public getRatingFormat(skill) {
    const numberPercent = this.decimalPipe.transform(
      skill.ratingPercent,
      '1.0-0'
    );

    return this.translate(
      '<span class="color-purple">[numberPercent]%</span> ([ratingsCount]) of team members rated this skill',
      'SkillSectionsChart_TeamMembers_RatingsFormat',
      {
        ratingsCount: skill.ratingCount,
        numberPercent,
      }
    );
  }

  public getAverageRatingFormat(skill) {
    const avg = this.decimalPipe.transform(skill.average, '1.1-1');

    return this.translate(
      '<span class="color-purple">[avg]</span> average rating',
      'SkillSectionsChart_AvgRatingFormat',
      { avg }
    );
  }
}
